import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useRef,
} from "react";
import {
  getAnswers,
  getAnswerLogs,
  getBlockTests,
  getBlockTestDetails,
  getBlockTestAnswers,
} from "../api/chatService";

const LogsContext = createContext();

export const useLogs = () => {
  const context = useContext(LogsContext);
  if (!context) {
    throw new Error("useLogs must be used within a LogsProvider");
  }
  return context;
};

export const LogsProvider = ({ children }) => {
  const [answers, setAnswers] = useState([]);
  const [blockTests, setBlockTests] = useState([]);
  const [answerLogsMap, setAnswerLogsMap] = useState({});
  const [blockTestDetailsMap, setBlockTestDetailsMap] = useState({});
  const [queryCache, setQueryCache] = useState({});
  const [isLoadingAnswers, setIsLoadingAnswers] = useState(false);
  const [isLoadingBlockTests, setIsLoadingBlockTests] = useState(false);
  const [isLoadingAnswerLogs, setIsLoadingAnswerLogs] = useState(false);
  const [isLoadingBlockTestDetails, setIsLoadingBlockTestDetails] =
    useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
  });
  const [blockTestsPagination, setBlockTestsPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
  });

  // Use refs to track in-flight requests
  const pendingAnswersRequest = useRef(false);
  const pendingBlockTestsRequest = useRef(false);
  const pendingLogsRequests = useRef({});
  const pendingBlockTestDetailsRequests = useRef({});
  const pendingSearchRequest = useRef(false);
  const pendingBlockTestSearchRequest = useRef(false);

  // Track fetched pages to avoid duplicate requests
  const [fetchedPages, setFetchedPages] = useState([]);
  const [fetchedBlockTestPages, setFetchedBlockTestPages] = useState([]);

  // Track whether we should set error state to avoid unnecessary updates
  const pendingErrorUpdate = useRef(null);

  // Set error safely to prevent update cycles
  const setErrorSafely = useCallback(
    (errorMessage) => {
      // Only set error if it's different from current error
      if (errorMessage !== error) {
        // Use setTimeout to break React update cycles
        setTimeout(() => {
          setError(errorMessage);
        }, 0);
      }
    },
    [error]
  );

  // Generate cache key based on query parameters
  const generateCacheKey = useCallback((page, limit, searchQuery = "") => {
    return `page=${page}_limit=${limit}_search=${searchQuery}`;
  }, []);

  // Fetch paginated answers
  const fetchAnswersForPage = useCallback(
    async (page, skipFetching = false) => {
      if (pendingAnswersRequest.current) {
        return;
      }

      const cacheKey = generateCacheKey(page, pagination.limit);

      // Check if we have cached results for this query
      if (queryCache[cacheKey]) {
        setAnswers(queryCache[cacheKey].answers);
        setPagination({
          ...queryCache[cacheKey].pagination,
          page, // Ensure we set the requested page
        });
        return;
      }

      if (fetchedPages.includes(page)) {
        // Update the current page without refetching
        setPagination((prev) => ({
          ...prev,
          page,
        }));
        return;
      }

      if (skipFetching) {
        // Just update pagination without fetching
        setPagination((prev) => ({
          ...prev,
          page,
        }));
        return;
      }

      setIsLoadingAnswers(true);
      setError(null);
      pendingAnswersRequest.current = true;

      try {
        const response = await getAnswers(page, pagination.limit);
        // Check the response structure and adapt accordingly
        const answersArray = response.answers;

        setAnswers(answersArray);

        setPagination({
          ...response.pagination,
          page, // Ensure the page is set correctly
        });

        // Cache the results
        setQueryCache((prev) => ({
          ...prev,
          [cacheKey]: {
            answers: answersArray,
            pagination: {
              ...response.pagination,
              page, // Store the correct page in cache
            },
            timestamp: Date.now(),
          },
        }));

        // Add this page to the list of fetched pages
        if (!fetchedPages.includes(page)) {
          setFetchedPages((prev) => [...prev, page]);
        }
      } catch (err) {
        setErrorSafely("Failed to fetch answers. Please try again later.");
      } finally {
        setIsLoadingAnswers(false);
        pendingAnswersRequest.current = false;
      }
    },
    [
      pagination.limit,
      fetchedPages,
      queryCache,
      generateCacheKey,
      setErrorSafely,
    ]
  );

  // Fetch paginated block tests with stability improvements
  const fetchBlockTestsForPage = useCallback(
    async (page, skipFetching = false) => {
      // Static variable to track if we're in the process of updating
      // This helps prevent loops from repeated error/state changes
      if (fetchBlockTestsForPage._isUpdating) {
        return;
      }

      // Return immediately if a request is already pending
      if (pendingBlockTestsRequest.current) {
        return;
      }

      // No-op if the function has encountered an error and we're not explicitly resetting
      if (fetchBlockTestsForPage.hasError && !skipFetching) {
        return;
      }

      // Mark that we're starting an update
      fetchBlockTestsForPage._isUpdating = true;

      try {
        // If we're requested to skip fetching, just update the current page
        if (skipFetching) {
          setBlockTestsPagination((prev) => ({
            ...prev,
            page,
          }));
          return;
        }

        // Use cache if available
        const cacheKey = generateCacheKey(
          page,
          blockTestsPagination.limit,
          "blockTests"
        );
        if (queryCache[cacheKey]) {
          setBlockTests(queryCache[cacheKey].blockTests);
          setBlockTestsPagination({
            ...queryCache[cacheKey].pagination,
            page,
          });
          return;
        }

        // If we've already fetched this page, just update the pagination
        if (fetchedBlockTestPages.includes(page)) {
          setBlockTestsPagination((prev) => ({
            ...prev,
            page,
          }));
          return;
        }

        // Set loading state and mark request as pending
        pendingBlockTestsRequest.current = true;
        setIsLoadingBlockTests(true);

        // Clear any previous errors for a fresh request
        if (pendingErrorUpdate.current) {
          clearTimeout(pendingErrorUpdate.current);
          pendingErrorUpdate.current = null;
        }
        setError(null);

        try {
          const response = await getBlockTests(
            page,
            blockTestsPagination.limit
          );

          // Mark that we've successfully fetched data
          fetchBlockTestsForPage.hasError = false;

          // Map API response fields to the expected format
          const blockTestsArray =
            response.test_blocks?.map((test) => ({
              id: test.id,
              name: test.block_name,
              organization: test.organization_name,
              chatCount: test.chat_count,
              createdAt: test.created_at,
            })) || [];

          // Update state with mapped data
          setBlockTests(blockTestsArray);
          setBlockTestsPagination({
            ...response.pagination,
            page,
          });

          // Cache the results - but only if component is still mounted
          // This helps prevent async state updates after unmount
          setQueryCache((prev) => ({
            ...prev,
            [cacheKey]: {
              blockTests: blockTestsArray,
              pagination: {
                ...response.pagination,
                page,
              },
              timestamp: Date.now(),
            },
          }));

          // Track that we've fetched this page
          if (!fetchedBlockTestPages.includes(page)) {
            setFetchedBlockTestPages((prev) => [...prev, page]);
          }
        } catch (err) {
          // Set the static error flag
          fetchBlockTestsForPage.hasError = true;

          // Use a safe way to set error - need to break potential update cycles
          setTimeout(() => {
            if (!fetchBlockTestsForPage._isUpdating) {
              setError("Failed to fetch block tests. Please try again later.");
            }
          }, 0);

          // Reset data to prevent stale UI
          setBlockTests([]);
          setBlockTestsPagination((prev) => ({
            ...prev,
            page,
            total: 0,
          }));
        } finally {
          // Always clean up loading state
          setIsLoadingBlockTests(false);
          pendingBlockTestsRequest.current = false;
        }
      } finally {
        // Clear the updating flag
        setTimeout(() => {
          fetchBlockTestsForPage._isUpdating = false;
        }, 0);
      }
    },
    [
      blockTestsPagination.limit,
      fetchedBlockTestPages,
      queryCache,
      generateCacheKey,
    ]
  );

  // Search answers with the API endpoint
  const searchAnswers = useCallback(
    async (searchParams, page, limit, cacheFlag) => {
      if (pendingSearchRequest.current) {
        return;
      }

      // Create a unique cache key based on search parameters
      const cacheKeyParts = [`page=${page}`, `limit=${limit}`];
      if (searchParams.search) {
        cacheKeyParts.push(`search=${searchParams.search}`);
      }
      if (searchParams.email) {
        cacheKeyParts.push(`email=${searchParams.email}`);
      }
      if (searchParams.orgid) {
        cacheKeyParts.push(`orgid=${searchParams.orgid}`);
      }
      const cacheKey = cacheKeyParts.join("_");

      // If cacheFlag is true or we have the data in cache, use the cached data
      if (cacheFlag || queryCache[cacheKey]) {
        if (queryCache[cacheKey]) {
          setAnswers(queryCache[cacheKey].answers);
          setPagination({
            ...queryCache[cacheKey].pagination,
            page, // Ensure we respect the requested page
          });
          return;
        } else if (cacheFlag) {
          // If cacheFlag is true but data not found in cache,
          // something went wrong. Continue with API request.
        }
      }

      setIsLoadingAnswers(true);
      setError(null);
      pendingSearchRequest.current = true;

      try {
        // Extract individual parameters for API call
        const apiSearchParam = searchParams.search || null;
        const apiStatusParam = null; // Not currently using status filtering
        const apiEmailParam = searchParams.email || null;
        const apiOrgIdParam = searchParams.orgid || null;

        const response = await getAnswers(
          page,
          limit,
          apiSearchParam,
          apiStatusParam,
          apiEmailParam,
          apiOrgIdParam
        );

        // Update answers with search results
        setAnswers(response.answers);

        // Update pagination with search results pagination
        setPagination({
          ...response.pagination,
          page, // Ensure the page is the one we requested
        });

        // Cache the search results
        setQueryCache((prev) => ({
          ...prev,
          [cacheKey]: {
            answers: response.answers,
            pagination: {
              ...response.pagination,
              page, // Store the page we requested in cache
            },
            timestamp: Date.now(),
          },
        }));
      } catch (err) {
        setErrorSafely("Failed to search answers. Please try again later.");
      } finally {
        setIsLoadingAnswers(false);
        pendingSearchRequest.current = false;
      }
    },
    [queryCache, setErrorSafely]
  );

  // Search block tests with the API endpoint
  const searchBlockTests = useCallback(
    async (searchParams, page, limit, cacheFlag) => {
      // Return immediately if a request is already pending
      if (pendingBlockTestSearchRequest.current) {
        return null;
      }

      // No-op if the function has encountered an error and we're not explicitly resetting
      if (searchBlockTests.hasError && !cacheFlag) {
        return null;
      }

      // Create a unique cache key
      const cacheKeyParts = [
        `page=${page}`,
        `limit=${limit}`,
        `type=blockTests`,
      ];
      if (searchParams.name) {
        cacheKeyParts.push(`name=${searchParams.name}`);
      }
      if (searchParams.orgid) {
        cacheKeyParts.push(`orgid=${searchParams.orgid}`);
      }
      const cacheKey = cacheKeyParts.join("_");

      // Use cache if available
      if (queryCache[cacheKey]) {
        setBlockTests(queryCache[cacheKey].blockTests);
        setBlockTestsPagination({
          ...queryCache[cacheKey].pagination,
          page,
        });
        return queryCache[cacheKey];
      } else if (cacheFlag) {
        // Non-critical warning when cache flag is true but no cache exists
      }

      // Set loading state and mark request as pending
      pendingBlockTestSearchRequest.current = true;
      setIsLoadingBlockTests(true);

      // Clear any previous errors for a fresh request
      if (pendingErrorUpdate.current) {
        clearTimeout(pendingErrorUpdate.current);
        pendingErrorUpdate.current = null;
      }
      setError(null);

      try {
        const response = await getBlockTests(
          page,
          limit,
          searchParams.name,
          searchParams.orgid
        );

        // Map API response fields to the expected format
        const blockTestsArray =
          response.test_blocks?.map((test) => ({
            id: test.id,
            name: test.block_name,
            organization: test.organization_name,
            chatCount: test.chat_count,
            createdAt: test.created_at,
          })) || [];

        // Set state and update cache
        setBlockTests(blockTestsArray);
        setBlockTestsPagination({
          ...response.pagination,
          page,
        });

        // Return matching set of results
        searchBlockTests.hasError = false;

        // Save to cache
        const cacheResult = {
          blockTests: blockTestsArray,
          pagination: response.pagination,
        };

        setQueryCache((prev) => ({
          ...prev,
          [cacheKey]: {
            ...cacheResult,
            timestamp: Date.now(),
          },
        }));

        return cacheResult;
      } catch (err) {
        // Set the static error flag
        searchBlockTests.hasError = true;

        // Use a non-reactive way to set error message
        setErrorSafely("Failed to search block tests. Please try again later.");

        // Reset data to prevent stale UI
        setBlockTests([]);
        setBlockTestsPagination((prev) => ({
          ...prev,
          page,
          total: 0,
        }));

        return null;
      } finally {
        // Always clean up loading state
        setIsLoadingBlockTests(false);
        pendingBlockTestSearchRequest.current = false;
      }
    },
    [queryCache, setErrorSafely]
  );

  // Fetch logs for a specific answer
  const fetchAnswerLogs = useCallback(
    async (answerId) => {
      // If request for this answerId is already in flight, return cached logs or empty array
      if (pendingLogsRequests.current[answerId]) {
        return answerLogsMap[answerId] || [];
      }

      // If we already have logs for this answer, return them
      if (answerLogsMap[answerId]) {
        return answerLogsMap[answerId];
      }

      setIsLoadingAnswerLogs(true);
      pendingLogsRequests.current[answerId] = true;

      try {
        const response = await getAnswerLogs(answerId);

        // Store the entire response instead of just logs
        setAnswerLogsMap((prev) => ({
          ...prev,
          [answerId]: response,
        }));

        return response;
      } catch (err) {
        setErrorSafely(`Failed to fetch logs for answer ${answerId}`);
        return [];
      } finally {
        setIsLoadingAnswerLogs(false);
        pendingLogsRequests.current[answerId] = false;
      }
    },
    [setErrorSafely, answerLogsMap]
  );

  // Fetch details for a specific block test
  const fetchBlockTestDetails = useCallback(
    async (blockTestId) => {
      // If request for this blockTestId is already in flight, return cached details or empty object
      if (pendingBlockTestDetailsRequests.current[blockTestId]) {
        return blockTestDetailsMap[blockTestId] || {};
      }

      // If we already have details for this block test, return them
      if (blockTestDetailsMap[blockTestId]) {
        return blockTestDetailsMap[blockTestId];
      }

      setIsLoadingBlockTestDetails(true);
      pendingBlockTestDetailsRequests.current[blockTestId] = true;

      try {
        const details = await getBlockTestDetails(blockTestId);

        setBlockTestDetailsMap((prev) => ({
          ...prev,
          [blockTestId]: details,
        }));

        return details;
      } catch (err) {
        setErrorSafely(`Failed to fetch details for block test ${blockTestId}`);
        return {};
      } finally {
        setIsLoadingBlockTestDetails(false);
        pendingBlockTestDetailsRequests.current[blockTestId] = false;
      }
    },
    [blockTestDetailsMap, setErrorSafely]
  );

  // Clear cache for a specific query or all cache
  const clearCache = useCallback((cacheKey = null) => {
    if (cacheKey) {
      setQueryCache((prev) => {
        const newCache = { ...prev };
        delete newCache[cacheKey];
        return newCache;
      });
    } else {
      setQueryCache({});
    }
  }, []);

  // Reset just block tests state without affecting answer logs
  const resetBlockTests = useCallback(() => {
    setBlockTests([]);
    setBlockTestDetailsMap({});
    setFetchedBlockTestPages([]);
    setBlockTestsPagination({
      page: 1,
      limit: 20,
      total: 0,
    });

    // Reset error state
    setError(null);

    // Cancel any pending error updates
    if (pendingErrorUpdate.current) {
      clearTimeout(pendingErrorUpdate.current);
      pendingErrorUpdate.current = null;
    }

    // Clear error flags to allow retries
    if (fetchBlockTestsForPage.hasError) {
      fetchBlockTestsForPage.hasError = false;
    }

    if (searchBlockTests.hasError) {
      searchBlockTests.hasError = false;
    }

    // Also clear block test related cache entries
    setQueryCache((prev) => {
      const newCache = { ...prev };
      // Remove cache entries related to block tests
      Object.keys(newCache).forEach((key) => {
        if (key.includes("blockTests") || key.includes("blockTest-")) {
          delete newCache[key];
        }
      });
      return newCache;
    });
  }, []);

  // Reset all state
  const reset = useCallback(() => {
    setAnswers([]);
    setBlockTests([]);
    setAnswerLogsMap({});
    setBlockTestDetailsMap({});
    setQueryCache({});
    setFetchedPages([]);
    setFetchedBlockTestPages([]);
    setPagination({
      page: 1,
      limit: 20,
      total: 0,
    });
    setBlockTestsPagination({
      page: 1,
      limit: 20,
      total: 0,
    });
    setError(null);
  }, []);

  // Update the fetchBlockTestAnswers function
  const fetchBlockTestAnswers = useCallback(
    async (blockId) => {
      if (!blockId) {
        return;
      }

      // Prevent duplicate requests for the same block ID
      const requestKey = `block_answers_${blockId}`;
      if (pendingBlockTestDetailsRequests.current[requestKey]) {
        return blockTestDetailsMap[blockId] || null;
      }

      setIsLoadingBlockTestDetails(true);
      setError(null);

      // Mark request as in progress
      pendingBlockTestDetailsRequests.current[requestKey] = true;

      try {
        const response = await getBlockTestAnswers(blockId);

        // Store in blockTestDetailsMap only if we got valid data
        if (response) {
          setBlockTestDetailsMap((prevMap) => ({
            ...prevMap,
            [blockId]: response,
          }));
        }

        return response;
      } catch (err) {
        setErrorSafely(
          `Failed to fetch answers for block test "${blockId}". Please try again later.`
        );
        return null;
      } finally {
        setIsLoadingBlockTestDetails(false);
        // Clear pending request flag with slight delay to prevent immediate retries
        setTimeout(() => {
          pendingBlockTestDetailsRequests.current[requestKey] = false;
        }, 500);
      }
    },
    [setErrorSafely, blockTestDetailsMap]
  );

  const value = {
    answers,
    blockTests,
    answerLogsMap,
    blockTestDetailsMap,
    isLoadingAnswers,
    isLoadingBlockTests,
    isLoadingAnswerLogs,
    isLoadingBlockTestDetails,
    error,
    pagination,
    blockTestsPagination,
    fetchAnswersForPage,
    fetchBlockTestsForPage,
    searchAnswers,
    searchBlockTests,
    fetchAnswerLogs,
    fetchBlockTestDetails,
    clearCache,
    reset,
    fetchedPages,
    fetchedBlockTestPages,
    generateCacheKey,
    queryCache,
    resetBlockTests,
    fetchBlockTestAnswers,
  };

  return <LogsContext.Provider value={value}>{children}</LogsContext.Provider>;
};
